import { Injectable } from '@angular/core';
import { xor } from 'lodash-es';

import {
  ComplementarySignatureRuleLogic,
  Field,
  SignatureLogicActions,
  SignatureRuleLogic,
} from '@site-mate/dashpivot-shared-library';

import { TmpI18NService } from 'app/i18n/tmp-i18n.service';

import {
  DropdownNames,
  ComplementaryDropdownNames,
} from './logic-rule/signature-logic-rule/signature-logic-rule-helpers';

@Injectable({
  providedIn: 'root',
})
export class ApprovalSignatureService {
  constructor(private readonly i18nService: TmpI18NService) {}

  hasIncompleteRule(approvalSignatures: Field[]) {
    const signaturesWithLogic =
      approvalSignatures.filter((signature) => signature.signatureRules?.length) ?? [];

    return signaturesWithLogic.some((signature) => this.validateRuleFields(signature.signatureRules));
  }

  isValidRule(rule: SignatureRuleLogic): boolean {
    return !!rule.type && !!rule.operator && !!rule.values?.length && !!rule.action;
  }

  validateRuleFields(rules: SignatureRuleLogic[]) {
    rules.forEach((rule) => {
      rule._invalidInputs = [];

      Object.values(DropdownNames).forEach((dropdownName) => {
        this.setInvalidInputs(rule, dropdownName);
      });

      if (rule._invalidInputs?.length) {
        rule._invalid = true;
        rule._invalidMessage = this.i18nService.getMessage('inputRequiredError');
      } else {
        rule._invalid = false;
        rule._invalidMessage = '';
      }

      if (rule.complementaryRules?.length) {
        rule.complementaryRules.forEach((complementaryRule) => {
          complementaryRule._invalidInputs = [];

          Object.values(ComplementaryDropdownNames).forEach((complementaryDropdownName) => {
            this.setInvalidInputs(complementaryRule, complementaryDropdownName);
          });

          if (complementaryRule._invalidInputs?.length) {
            complementaryRule._invalid = true;
            complementaryRule._invalidMessage = this.i18nService.getMessage('inputRequiredError');
          } else {
            complementaryRule._invalid = false;
            complementaryRule._invalidMessage = '';
          }
        });

        this.setInvalidDuplicatedComplementaryRules(rule);
      }
    });

    this.isActionsInvalid(rules);

    const invalidComplementaryRules = rules.some((rule) =>
      rule.complementaryRules?.some((complementaryRule) => complementaryRule._invalid),
    );

    return rules.some((rule) => rule._invalidInputs?.length) || invalidComplementaryRules;
  }

  isActionsInvalid(rules: SignatureRuleLogic[]): void {
    const actions = rules?.map((rule) => rule.action);
    const isNotUniqueActions = new Set(actions).size !== actions.length;
    const hasGroupedAction = actions.find(
      (action) => action === SignatureLogicActions.ApprovalSignatureAndResetWorkflow,
    );

    if ((actions.length > 1 && hasGroupedAction) || isNotUniqueActions) {
      rules.forEach((rule) => {
        rule._invalidInputs.push(`action-${rule._id}`);
        rule._invalid = true;
        rule._invalidMessage = this.i18nService.getMessage('templateDuplicatedLogicActions');
      });
    }
  }

  setInvalidDuplicatedComplementaryRules(parentRule: SignatureRuleLogic): void {
    const { complementaryRules } = parentRule;
    const duplicatedComplementaryRules = complementaryRules.filter((complementaryRule, index) => {
      if (!this.isValidComplementaryRule(complementaryRule)) {
        return false;
      }

      const isDuplicated = complementaryRules.some(
        (rule, ruleIndex) =>
          ruleIndex !== index &&
          rule.type === complementaryRule.type &&
          rule.operator === complementaryRule.operator &&
          xor(rule.values, complementaryRule.values).length === 0,
      );

      return isDuplicated;
    });

    complementaryRules.forEach((complementaryRule) => {
      if (!this.isValidComplementaryRule(complementaryRule)) {
        return;
      }
      const isDuplicated = duplicatedComplementaryRules.some(
        (duplicatedRule) => duplicatedRule._id === complementaryRule._id,
      );
      const duplicatedParentRule = this.isParentRuleDuplicated(parentRule, complementaryRule);

      if (isDuplicated || duplicatedParentRule) {
        const inputs = ['type', 'operator', 'values'];
        inputs.forEach((input) => complementaryRule._invalidInputs.push(`${input}-${complementaryRule._id}`));

        complementaryRule._invalid = true;
        complementaryRule._invalidMessage = this.i18nService.getMessage(
          'templateDuplicatedComplementaryRule',
        );
      }
    });
  }

  private isValidComplementaryRule(rule: ComplementarySignatureRuleLogic): boolean {
    return !!rule.type && !!rule.operator && !!rule.values?.length;
  }

  private isParentRuleDuplicated(
    parentRule: SignatureRuleLogic,
    complementaryRule: ComplementarySignatureRuleLogic,
  ) {
    return (
      parentRule.type === complementaryRule.type &&
      parentRule.operator === complementaryRule.operator &&
      xor(parentRule.values, complementaryRule.values).length === 0
    );
  }

  private setInvalidInputs(
    rule: SignatureRuleLogic | ComplementarySignatureRuleLogic,
    property: string,
  ): void {
    const isMissingProperty = !rule[property];
    const isArrayEmpty = Array.isArray(rule[property]) && !rule[property].length;

    if (isMissingProperty || isArrayEmpty) {
      rule._invalidInputs.push(`${property}-${rule._id}`);
    }
  }
}
